import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, CurrencyCircleDollar } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const LANGUAGES = [
  { code: 'nb', label: 'Norsk', flag: '🇳🇴' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'sv', label: 'Svenska', flag: '🇸🇪' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰' },
];

const CURRENCIES = [
  { code: 'NOK', symbol: 'kr', label: 'Norske kroner' },
  { code: 'SEK', symbol: 'kr', label: 'Svenska kronor' },
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
  { code: 'USD', symbol: '$', label: 'US Dollar' },
];

export default function LanguageCurrencySwitcher() {
  const { i18n } = useTranslation();
  const [currency, setCurrency] = React.useState(localStorage.getItem('currency') || 'EUR');

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[1];
  const currentCurrency = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[1];

  const setLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('i18nextLng', code);
  };
  const setCurrencyAndPersist = (code) => {
    setCurrency(code);
    localStorage.setItem('currency', code);
    window.dispatchEvent(new CustomEvent('currencychange', { detail: code }));
  };

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1.5 h-8 px-2" data-testid="lang-switcher">
            <Globe size={14} />
            <span className="text-xs">{currentLang.flag} {currentLang.code.toUpperCase()}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="text-xs">Språk / Language</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {LANGUAGES.map((l) => (
            <DropdownMenuItem
              key={l.code}
              onClick={() => setLanguage(l.code)}
              data-testid={`lang-${l.code}`}
              className="gap-2"
            >
              <span>{l.flag}</span>
              <span className="text-sm">{l.label}</span>
              {l.code === i18n.language && <span className="ml-auto text-primary">✓</span>}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1.5 h-8 px-2" data-testid="currency-switcher">
            <CurrencyCircleDollar size={14} />
            <span className="text-xs">{currentCurrency.code}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="text-xs">Valuta / Currency</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {CURRENCIES.map((c) => (
            <DropdownMenuItem
              key={c.code}
              onClick={() => setCurrencyAndPersist(c.code)}
              data-testid={`currency-${c.code}`}
              className="gap-2"
            >
              <span className="font-mono w-6">{c.symbol}</span>
              <span className="text-sm">{c.code}</span>
              <span className="text-xs text-muted-foreground ml-auto">{c.label}</span>
              {c.code === currency && <span className="text-primary">✓</span>}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
